import { createSelector } from 'reselect';
import sortBy from 'lodash/sortBy';
import type {
	Article,
	Category,
	GenericError,
	ArticlePersistedState,
	CategoryGuid,
} from '@atlassian/jira-servicedesk-knowledge-services/src/types.tsx';
import type { CategoriesState } from '../../entities/categories/types.tsx';
import type { State } from '../../types.tsx';
import { getCategory } from '../categories/index.tsx';

export const getFeaturedInCategoryIds = (
	state: State,
	articleId: string,
): CategoryGuid[] | undefined =>
	state.entities.articles[articleId] &&
	// @ts-expect-error - TS2339 - Property 'featuredInCategories' does not exist on type 'ArticleState'.
	state.entities.articles[articleId].featuredInCategories &&
	// @ts-expect-error - TS2339 - Property 'featuredInCategories' does not exist on type 'ArticleState'.
	state.entities.articles[articleId].featuredInCategories;

const getArticleCategories = (allCategories: CategoriesState, article: ArticlePersistedState) => {
	if (article.categories) {
		const categories = article.categories
			.map((categoryId) => allCategories.categories.find((category) => category.id === categoryId))
			.filter(Boolean);

		const featuredInCategoryIds = article.featuredInCategories || [];
		if (featuredInCategoryIds.length === 0) {
			return categories;
		}

		return sortBy(categories, (cat) => !featuredInCategoryIds.includes(cat.id));
	}

	return undefined;
};

export const getArticle = createSelector(
	(state: State, articleId: string) => state.entities.articles[articleId],
	(state: State) => state.entities.categories,
	(articleOrError, allCategories): Article | GenericError | undefined => {
		if (!articleOrError) {
			return undefined;
		}

		if ('error' in articleOrError) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return articleOrError as GenericError;
		}

		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
		const article: ArticlePersistedState = articleOrError as any;
		const categories = getArticleCategories(allCategories, article);
		const { categories: _, ...articleWithoutCategory } = article;
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return { ...articleWithoutCategory, categories } as Article;
	},
);

const getUpdatingCategoriesForArticle = (state: State, articleId: string): Category[] | undefined =>
	state.ui.articles[articleId] &&
	state.ui.articles[articleId].categoryIds &&
	state.ui.articles[articleId].categoryIds.map((id) => getCategory(state, id)).filter(Boolean);

export const getCategoriesForArticle = (state: State, articleId: string): Category[] => {
	const article = getArticle(state, articleId);
	if (!article) {
		return [];
	}
	// @ts-expect-error - TS2339 - Property 'error' does not exist on type 'Article | GenericError'.
	if (article.error) {
		return [];
	}
	// @ts-expect-error - TS2339 - Property 'categories' does not exist on type 'Article | GenericError'.
	return article.categories || [];
};

// this selector is currently used for bulk update categories component to
// have opstimictic update, we might want to use this for all other place
// that try to fetch categories for article
export const getCategoriesForArticleBulkUpdate = (state: State, articleId: string): Category[] => {
	const updatingCategories = getUpdatingCategoriesForArticle(state, articleId);

	// if there are updating categories value, we will use the updating state so it will
	// always reflect the latest categories for article
	if (updatingCategories) {
		return updatingCategories;
	}

	// retrieve from persisted state if there is no updating value available
	return getCategoriesForArticle(state, articleId);
};

export const getArticlesByIds = (state: State, articleIds: string[]): (Article | GenericError)[] =>
	articleIds.map((articleId) => getArticle(state, articleId)).filter(Boolean);

export const getIsShowingBulkCategoryAdder = (state: State, articleId: string): boolean =>
	state.ui.articles[articleId] && !!state.ui.articles[articleId].isShowingBulkCategoryAdder;

export const getCategoriesInBulkCategoryAdder = (
	state: State,
	articleId: string,
): CategoryGuid[] => {
	if (!state.ui.articles[articleId]) {
		return [];
	}
	return state.ui.articles[articleId].categoriesInBulkCategoryAdder || [];
};

export const getSpaceKeyForArticle = (state: State, articleId: string): string => {
	const article = getArticle(state, articleId);
	if (!article) {
		return '';
	}
	// @ts-expect-error - TS2339 - Property 'spaceKey' does not exist on type 'Article | GenericError'.
	if (article.spaceKey) {
		// @ts-expect-error - TS2339 - Property 'spaceKey' does not exist on type 'Article | GenericError'.
		return article.spaceKey;
	}
	return '';
};
